import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook for managing companies across databases
 */
export function useCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);

  /**
   * Get consolidated companies (from all databases)
   */
  const listConsolidated = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get('/companies/consolidated', { params: filters });
      setCompanies(data.data || []);
      setTotal(data.total || 0);
      return { companies: data.data, total: data.total, errors: data.errors };
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      console.error('Failed to list consolidated companies:', message);
      return { companies: [], total: 0 };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get companies from a specific database
   */
  const listByDatabase = useCallback(async (databaseId, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/companies/${databaseId}`, { params: filters });
      setCompanies(data.data || []);
      setTotal(data.total || 0);
      return { companies: data.data, total: data.total, database: data.database };
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      console.error('Failed to list companies:', message);
      return { companies: [], total: 0 };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get a specific company from a database
   */
  const getById = useCallback(async (databaseId, companyId) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get(`/companies/${databaseId}/${companyId}`);
      return data.data;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update a company in a database
   */
  const update = useCallback(async (databaseId, companyId, updates) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.put(`/companies/${databaseId}/${companyId}`, updates);
      
      // Update in local list if exists
      setCompanies((prev) =>
        prev.map((company) => 
          company.id === companyId ? { ...company, ...data.data } : company
        )
      );
      return data.data;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a company from a database
   */
  const remove = useCallback(async (databaseId, companyId) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.delete(`/companies/${databaseId}/${companyId}`);
      
      // Remove from local list
      setCompanies((prev) => prev.filter((company) => company.id !== companyId));
      return data;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(async (databaseId, companyData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post(`/companies/${databaseId}`, companyData);
      const newCompany = response.data.data;
      setCompanies([...companies, newCompany]);
      return newCompany;
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, [companies]);

  return {
    companies,
    loading,
    error,
    total,
    listConsolidated,
    listByDatabase,
    getById,
    create,
    update,
    remove,
  };
}
